import { ReactElement, useState, useEffect } from 'react';
import { CreateTeamUserRequest, CompanyUser } from '@/api';
import { useCompanyContext } from '../providers';
import { useAddTeamUserAction } from '../actions';
import { useFetchUsersPaginated } from '../queries';
import {
    UIModal,
    UIButton,
    UIEmptyState,
    UIErrorState,
    UIFormInput,
    UILoadingState,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISpinner
} from '@/ui';
import { useDebouncedValue } from '@/common/hooks';
import { useInView } from 'react-intersection-observer';
import { charcoal } from '@/ui/styles';
import { UserInfoListItem } from './user-info-list-item';
import { useCompaniesTranslation } from '../hooks';

type AddTeamUsersModalProps = {
    teamId: string;
    isOpen: boolean;
    onClose: () => void;
};

export function AddTeamUsersModal(props: AddTeamUsersModalProps): ReactElement {
    const { isOpen, onClose } = props;

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen && <AddTeamUsersModal2 {...props} />}
        </UIModal>
    );
}

function AddTeamUsersModal2(props: AddTeamUsersModalProps): ReactElement {
    const { teamId, isOpen, onClose } = props;

    const { company } = useCompanyContext();
    const { addTeamUser, loading } = useAddTeamUserAction();
    const [companyUserIdLoading, setCompanyUserIdLoading] = useState<
        string | null
    >(null);

    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebouncedValue(searchQuery.trim());

    // Company members not yet on this team. After a user is added, the
    // central cache invalidation refetches this query and they drop out.
    const {
        users,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchUsersPaginated({
        companyId: company.id,
        search: debouncedSearch,
        excludeTeamIds: [teamId],
        membership: 'all',
        sortBy: 'name',
        sortOrder: 'asc'
    });

    function handleClose() {
        onClose();
    }

    function onSubmit(companyUserId: string, data: CreateTeamUserRequest) {
        setCompanyUserIdLoading(companyUserId);
        addTeamUser(companyUserId, data)
            .finally(() => {
                setCompanyUserIdLoading(null);
            })
            .catch(() => {}); // Error handled in hook
    }

    return (
        <AddTeamUsersModalForm
            teamId={teamId}
            users={users}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            hasSearch={debouncedSearch !== ''}
            status={status}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            onRetry={refetch}
            isOpen={isOpen}
            onClose={handleClose}
            loading={loading}
            companyUserIdLoading={companyUserIdLoading}
            onSubmit={onSubmit}
        />
    );
}

type AddTeamUsersModalFormProps = {
    teamId: string;
    users: CompanyUser[];
    searchQuery: string;
    onSearchChange: (query: string) => void;
    hasSearch: boolean;
    status: 'error' | 'success' | 'pending';
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    onRetry: () => void;
    isOpen: boolean;
    loading: boolean;
    companyUserIdLoading: string | null;
    onClose: () => void;
    onSubmit: (companyUserId: string, data: CreateTeamUserRequest) => void;
};

function AddTeamUsersModalForm(
    props: AddTeamUsersModalFormProps
): ReactElement {
    const { isOpen } = props;

    return <>{isOpen ? <FormContent {...props} /> : null}</>;
}

function FormContent(props: AddTeamUsersModalFormProps): ReactElement {
    const {
        teamId,
        users,
        searchQuery,
        onSearchChange,
        hasSearch,
        status,
        hasNextPage,
        isFetchingNextPage,
        onLoadMore,
        onRetry,
        loading,
        companyUserIdLoading,
        onClose,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    // Load the next page when the bottom of the scroll list comes into view.
    const { ref: bottomRef, inView } = useInView({ threshold: 0 });
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            onLoadMore();
        }
    }, [inView, hasNextPage, isFetchingNextPage, onLoadMore]);

    function clearSearch() {
        onSearchChange('');
    }

    return (
        <>
            <UIModalHeader title={t('components.add_team_users_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0
                    }}
                >
                    <UIFormInput
                        value={searchQuery}
                        placeholder={t('shared.labels.search')}
                        height={20}
                        onChange={onSearchChange}
                    />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                            overflowY: 'auto',
                            maxHeight: 400,
                            paddingTop: 16
                        }}
                    >
                        {status === 'pending' && <UILoadingState />}
                        {status === 'error' && (
                            <UIErrorState
                                title={t('shared.errors.load_members')}
                                onRetry={onRetry}
                            />
                        )}
                        {status === 'success' &&
                            users.map((user) => (
                                <div
                                    key={user.id}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        gap: 16
                                    }}
                                >
                                    <UserInfoListItem user={user} />
                                    <UIButton
                                        variant="default"
                                        loading={
                                            loading &&
                                            companyUserIdLoading === user.id
                                        }
                                        disabled={loading}
                                        onClick={() => {
                                            onSubmit(user.id, {
                                                teamId,
                                                roles: {}
                                            });
                                        }}
                                    >
                                        {t('shared.labels.add')}
                                    </UIButton>
                                </div>
                            ))}

                        {status === 'success' &&
                            users.length === 0 &&
                            hasSearch && (
                                <UIEmptyState
                                    description={t('shared.labels.no_results')}
                                    buttonText={t('shared.labels.clear_search')}
                                    emoji="😔"
                                    onClick={clearSearch}
                                />
                            )}
                        {status === 'success' &&
                            users.length === 0 &&
                            !hasSearch && (
                                <UIEmptyState
                                    description={t(
                                        'components.add_team_users_modal.none_available'
                                    )}
                                    emoji="😔"
                                />
                            )}
                        {status === 'success' && hasNextPage && (
                            <div
                                ref={bottomRef}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: 8
                                }}
                            >
                                {isFetchingNextPage && (
                                    <UISpinner color={charcoal} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        text: t('shared.labels.close'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}
